import { type ComponentType, memo, useCallback, useState } from "react";

import { Checkbox } from "@/components/_atoms/checkbox";
import { Input } from "@/components/_atoms/input";
import { Textarea } from "@/components/_atoms/textarea";
import { InputDate } from "@/components/_molecules/input-date/input-date";
import { useDomainTranslation } from "@/domain/_shared/i18n/use-domain-translation";

import { toDisplayProjectDate, toPersistedProjectDate } from "../../lib/project-date-offset";
import type { DataRequestFieldResponse, SaveFieldHandler } from "../../models/data-request.model";
import type { DataRequestFieldType } from "../create-data-request-dialog/data-request-fields";

import { FocusAreaSelectField } from "./focus-area-select-field";
import { FrequencySelectField } from "./frequency-select-field";
import { LabelSelectField } from "./label-select-field";
import { PhaseSelectField } from "./phase-select-field";
import { PrioritySelectField } from "./priority-select-field";
import { resolveFieldInitialValue } from "./resolve-field-initial-value";
import { RichTextFieldCell } from "./rich-text-field-cell";
import type { SelectFieldProps } from "./select-field-props";
import { StatusSelectField } from "./status-select-field";
import { UnitSelectField } from "./unit-select-field";
import type { FieldDraft } from "./use-draft-persistence";
import { UserSelectField } from "./user-select-field";

const PROJECT_DATE_FIELD_KEYS = new Set(["startDate", "endDate"]);

const WORKSPACE_SELECT_FIELDS: Partial<Record<DataRequestFieldType, ComponentType<SelectFieldProps>>> = {
  "status-select": StatusSelectField,
  "priority-select": PrioritySelectField,
  "phase-select": PhaseSelectField,
  "focus-area-select": FocusAreaSelectField,
  "user-select": UserSelectField,
  "label-select": LabelSelectField,
  "unit-select": UnitSelectField,
};

interface DataEntryFieldRowProps {
  fieldKey: string;
  labelKey: string;
  fieldType: DataRequestFieldType;
  workspaceId: string;
  currentValue: string | null;
  defaultValue: string | null;
  rawDescription?: string | null;
  savedResponse: DataRequestFieldResponse | null;
  draft: FieldDraft | null;
  onSave: SaveFieldHandler;
  disabled?: boolean;
  minDate?: Date;
  maxDate?: Date;
}

function DataEntryFieldRowComponent({
  fieldKey,
  labelKey,
  fieldType,
  workspaceId,
  currentValue,
  defaultValue,
  rawDescription,
  savedResponse,
  draft,
  onSave,
  disabled,
  minDate,
  maxDate,
}: DataEntryFieldRowProps) {
  const { t } = useDomainTranslation("data-request");

  const [newValue, setNewValue] = useState<string>(() =>
    resolveFieldInitialValue({ draft, savedResponse, defaultValue }),
  );
  const [notPossible, setNotPossible] = useState(() => draft?.notPossible ?? savedResponse?.notPossible ?? false);
  const [comment, setComment] = useState(() => draft?.comment ?? savedResponse?.comment ?? "");
  const [valueResetKey, setValueResetKey] = useState(0);

  const emitSave = useCallback(
    (next: { newValue: string; notPossible: boolean; comment: string }) => {
      onSave({
        fieldKey,
        newValue: next.newValue === "" ? null : next.newValue,
        notPossible: next.notPossible,
        comment: next.comment === "" ? null : next.comment,
      });
    },
    [fieldKey, onSave],
  );

  const handleValueChange = useCallback(
    (value: string) => {
      setNewValue(value);
      emitSave({ newValue: value, notPossible, comment });
    },
    [notPossible, comment, emitSave],
  );

  const handleNotPossibleChange = useCallback(
    (checked: boolean) => {
      setNotPossible(checked);
      if (checked) {
        setNewValue("");
        setValueResetKey((previous) => previous + 1);
      }
      emitSave({ newValue: checked ? "" : newValue, notPossible: checked, comment });
    },
    [newValue, comment, emitSave],
  );

  const handleCommentChange = useCallback(
    (value: string) => {
      setComment(value);
      emitSave({ newValue, notPossible, comment: value });
    },
    [newValue, notPossible, emitSave],
  );

  const isInputDisabled = disabled || notPossible;

  const renderInput = () => {
    const WorkspaceSelect = WORKSPACE_SELECT_FIELDS[fieldType];
    if (WorkspaceSelect) {
      return (
        <WorkspaceSelect workspaceId={workspaceId} value={newValue} onChange={handleValueChange} disabled={isInputDisabled} />
      );
    }

    switch (fieldType) {
      case "richtext":
        return (
          <RichTextFieldCell
            value={newValue}
            rawDescription={rawDescription}
            labelKey={labelKey}
            disabled={isInputDisabled}
            onSave={handleValueChange}
          />
        );
      case "textarea":
        return (
          <Textarea
            value={newValue}
            onChange={(e) => handleValueChange(e.target.value)}
            disabled={isInputDisabled}
            className="min-h-16 text-sm"
            placeholder={t("dataEntry.fieldTable.noChange")}
            aria-label={t("dataEntry.fieldTable.noChange")}
            rows={2}
          />
        );
      case "date": {
        const isProjectDate = PROJECT_DATE_FIELD_KEYS.has(fieldKey);
        const inputDefault = isProjectDate ? toDisplayProjectDate(newValue)?.toISOString() : newValue || undefined;
        return (
          <InputDate
            key={valueResetKey}
            defaultValue={inputDefault}
            onValueChange={(val) => handleValueChange(val ? (isProjectDate ? toPersistedProjectDate(val) : val) : "")}
            disabled={isInputDisabled}
            className="h-8 text-sm"
            minDate={minDate}
            maxDate={maxDate}
          />
        );
      }
      case "number":
        return (
          <Input
            type="number"
            value={newValue}
            onChange={(e) => handleValueChange(e.target.value)}
            disabled={isInputDisabled}
            className="h-8 text-sm"
            placeholder={t("dataEntry.fieldTable.noChange")}
            aria-label={t("dataEntry.fieldTable.noChange")}
          />
        );
      case "frequency-select":
        return <FrequencySelectField value={newValue} onChange={handleValueChange} disabled={isInputDisabled} />;
      default:
        return (
          <Input
            value={newValue}
            onChange={(e) => handleValueChange(e.target.value)}
            disabled={isInputDisabled}
            className="h-8 text-sm"
            placeholder={t("dataEntry.fieldTable.noChange")}
            aria-label={t("dataEntry.fieldTable.noChange")}
          />
        );
    }
  };

  return (
    <tr className="border-b last:border-b-0">
      <td className="truncate px-3 py-2 text-sm font-medium">{t(labelKey)}</td>
      <td className="px-3 py-2 text-sm text-muted-foreground">
        <span className="line-clamp-2 wrap-break-word">{currentValue ?? ""}</span>
      </td>
      <td className="px-3 py-2">{renderInput()}</td>
      <td className="px-3 py-2 text-center">
        <Checkbox
          checked={notPossible}
          onCheckedChange={(checked) => handleNotPossibleChange(!!checked)}
          disabled={disabled}
          aria-label={t("dataEntry.fieldTable.notPossibleFor", { field: t(labelKey) })}
        />
      </td>
      <td className="px-3 py-2">
        <Input
          value={comment}
          onChange={(e) => handleCommentChange(e.target.value)}
          disabled={disabled}
          className="h-8 text-sm"
          placeholder={t("dataEntry.fieldTable.comment")}
          aria-label={t("dataEntry.fieldTable.comment")}
        />
      </td>
    </tr>
  );
}

export const DataEntryFieldRow = memo(DataEntryFieldRowComponent);
